/**
 * My Space / Organization — the merged content browser.
 *
 * Both views are the same two things (applications, layers), told apart
 * only by `scope`: `mine` shows the signed-in user's own items PLUS
 * whatever another owner has shared with them directly — labelled "Shared
 * by {owner}" rather than "Owned by", and without that owner's own
 * "shared with" list, which is their business, not the recipient's (true
 * for an admin too — their My Space is not the whole portal), `all` shows
 * every owner's (reachable only with `modules:view-all`, which an admin
 * already holds and anyone else can be granted — see SettingsPage's own
 * gating).
 *
 * A thin wrapper, not a rewrite: `AppGalleryTab` carries reorder/thumbnail/
 * home-page state a layer has no equivalent for, and the two already have
 * different detail views (the Builder vs. a "Layer information" panel) —
 * merging their row shapes into one list would risk both for no real gain.
 * This filters `modules` by scope, loads the user roster ONCE (the same
 * list `ShareModuleDialog` already loads, reused here for owner names and
 * "shared with" names at no extra fetch cost), and renders the two
 * existing tabs as sub-views of one page.
 */

import { useEffect, useMemo, useState } from 'react';
import { Building2, User as UserIcon, Image as ImageIcon, Layers as LayersIcon } from 'lucide-react';
import type { AppModule, User } from '../../types';
import { useAuth } from '../../auth/AuthContext';
import { userService } from '../../services/userService';
import { layerLibraryService, type OrgSharedLayerEntry } from '../../services/layerLibraryService';
import AppGalleryTab from './AppGalleryTab';
import LayersRegistryTab from './LayersRegistryTab';

type Scope = 'mine' | 'all';
type SubView = 'apps' | 'layers';

export default function ContentSpaceTab({
  scope,
  modules,
  onOpenModule,
  onEditModule,
  onShareModule,
  onDeleteModule,
  onModulesChanged,
  canShareModules = false,
}: {
  scope: Scope;
  modules: AppModule[];
  onOpenModule: (id: string) => void;
  onEditModule: (id: string) => void;
  onShareModule: (id: string) => void;
  onDeleteModule: (id: string) => void;
  onModulesChanged: (modules: AppModule[]) => void;
  /** Whether the caller's own role holds `modules:share` — passed straight
   *  through to `AppGalleryTab`'s own `getModuleAccess` check. */
  canShareModules?: boolean;
}) {
  const { currentUser } = useAuth();
  const [subView, setSubView] = useState<SubView>('apps');
  const [users, setUsers] = useState<User[]>([]);
  const [orgSharedLayers, setOrgSharedLayers] = useState<OrgSharedLayerEntry[]>([]);

  // Loaded once per mount regardless of scope — owner names need it in
  // Organization, "shared with" names need it in both (a module/layer this
  // person owns can already be shared from My Space).
  useEffect(() => {
    let live = true;
    void userService
      .listUsers()
      .then((list) => {
        if (live) setUsers(list);
      })
      .catch(() => {
        if (live) setUsers([]);
      });
    return () => {
      live = false;
    };
  }, []);

  // Organization only: layers someone shared with the whole org, portal-wide
  // — the one place `modules` (already scoped to what this viewer can see)
  // is not enough, since an org-shared layer's own module may not be.
  useEffect(() => {
    if (scope !== 'all') {
      setOrgSharedLayers([]);
      return;
    }
    let live = true;
    void layerLibraryService
      .listOrgSharedLayers()
      .then((list) => {
        if (live) setOrgSharedLayers(list);
      })
      .catch(() => {
        if (live) setOrgSharedLayers([]);
      });
    return () => {
      live = false;
    };
  }, [scope]);

  // My Space now shows what the viewer owns PLUS what's been shared directly
  // with them (a group grant surfaces via the portal home page instead,
  // same as before — this only widens the direct-share case).
  const scoped = useMemo(
    () =>
      scope === 'mine'
        ? modules.filter(
            (module) =>
              module.ownerId === currentUser?.id ||
              currentUser?.assignedModuleIds?.includes(module.id),
          )
        : modules,
    [modules, scope, currentUser?.id, currentUser?.assignedModuleIds],
  );

  const usersById = useMemo(() => new Map(users.map((user) => [user.id, user.name])), [users]);

  const isOwnModule = (module: AppModule) => module.ownerId === currentUser?.id;

  // null suppresses the line entirely — still true for a My Space row the
  // viewer owns themselves (every row used to be theirs; naming them on
  // their own app is noise). A My Space row they DON'T own (shared with
  // them) now returns the name same as Organization does, and AppGalleryTab
  // labels it "Shared by" rather than "Owned by" via `getModuleAccess`'s own
  // `isOwner` flag — the same source of truth the 3-dot menu's gating uses.
  const ownerNameForModule = (module: AppModule): string | null => {
    if (scope === 'mine' && isOwnModule(module)) return null;
    return (module.ownerId && usersById.get(module.ownerId)) || 'Unknown owner';
  };
  // Who else a module is shared with is owner/admin information — a My
  // Space row the viewer does not own (it's there because it was shared
  // WITH them) skips this line entirely.
  const sharedWithNamesForModule = (module: AppModule) =>
    scope === 'mine' && !isOwnModule(module)
      ? []
      : users.filter((user) => user.assignedModuleIds.includes(module.id)).map((user) => user.name);
  // Layers registry only ever calls this for scope 'all' (see below), where
  // `ownerNameForModule` never returns null — the fallback is unreachable,
  // just satisfying this callback's own (non-nullable) contract.
  const ownerNameForLayerEntry = (entry: { sampleModule: AppModule }) =>
    ownerNameForModule(entry.sampleModule) ?? 'Unknown owner';

  const appCount = scoped.length;
  const SubTabButton = ({ id, label, Icon }: { id: SubView; label: string; Icon: typeof ImageIcon }) => (
    <button
      type="button"
      onClick={() => setSubView(id)}
      className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12.5px] font-medium transition ${
        subView === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
        {scope === 'mine' ? (
          <UserIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
        ) : (
          <Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
        )}
        <p className="text-[11.5px] leading-relaxed text-slate-500">
          {scope === 'mine'
            ? 'What you own, plus anything another owner has shared directly with you — view only unless it’s yours. Something shared with a GROUP you belong to still lives on the portal home page, not here.'
            : 'What you own, what’s been shared with you directly, and anything another owner has turned on "Share with Organization" for — view only unless it’s yours.'}
        </p>
      </div>

      <div className="flex gap-1 rounded-md bg-slate-100 p-0.5">
        <SubTabButton id="apps" label={`Applications (${appCount})`} Icon={ImageIcon} />
        <SubTabButton id="layers" label="Layers" Icon={LayersIcon} />
      </div>

      {subView === 'apps' ? (
        <AppGalleryTab
          modules={scoped}
          onOpen={onOpenModule}
          onEdit={onEditModule}
          onShare={onShareModule}
          onDelete={onDeleteModule}
          onModulesChanged={onModulesChanged}
          // Held back until the user roster has actually loaded — otherwise
          // every row would flash "Unknown owner" for the one request's
          // round trip before settling on the right name. Passed for BOTH
          // scopes now — My Space's own function already returns null for a
          // row the viewer owns, so this changes nothing there except on the
          // shared-with-me rows it didn't used to have.
          ownerName={users.length > 0 ? ownerNameForModule : undefined}
          sharedWithNames={users.length > 0 ? sharedWithNamesForModule : undefined}
          canShareModules={canShareModules}
        />
      ) : (
        <LayersRegistryTab
          modules={scoped}
          onOpenApp={onEditModule}
          ownerName={scope === 'all' && users.length > 0 ? ownerNameForLayerEntry : undefined}
          users={users}
          orgSharedLayers={scope === 'all' ? orgSharedLayers : undefined}
        />
      )}
    </div>
  );
}
